import React, { useState } from "react";
import { MapPin, Phone, Mail, FileText, Send, MessageCircle } from "lucide-react";
import { CONTACT, GALLERY } from "../mock";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const subject = `Stay Enquiry from ${form.name || "Website Visitor"}`;
    const body =
      `Name: ${form.name}\n` +
      `Email: ${form.email}\n\n` +
      `${form.message}`;
    const mailto = `mailto:${CONTACT.email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setSent(true);
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-neutral-50">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900 mb-4">
            Memorable Stay
          </h2>
          <p className="text-neutral-600 max-w-3xl mx-auto leading-8">
            Whether you’re planning a one-day visit, a week-long honeymoon trip, or a month-long
            staycation, we’re here to make your stay memorable. Contact us to book your stay and make
            the most of your time in Munnar!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Info */}
          <div className="space-y-5">
            {[
              { icon: MapPin, label: "Our Address", value: CONTACT.address },
              { icon: Phone, label: "Contact Number", value: CONTACT.phone },
              { icon: Mail, label: "Email Address", value: CONTACT.email },
              { icon: FileText, label: "GSTIN/UIN", value: CONTACT.gstin },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 flex gap-4 shadow-sm">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-[#e3faf7] flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-[#17a89b]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1">
                    {item.label}
                  </p>
                  <p className="text-neutral-800 leading-7">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            <h3 className="text-xl font-bold text-neutral-900 mb-6">Book Your Stay</h3>
            <form onSubmit={submit} className="space-y-4">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your Name"
                className="w-full border border-neutral-200 rounded-lg px-4 py-3 outline-none focus:border-[#17a89b] focus:ring-2 focus:ring-[#17a89b]/20 transition"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Your Email"
                className="w-full border border-neutral-200 rounded-lg px-4 py-3 outline-none focus:border-[#17a89b] focus:ring-2 focus:ring-[#17a89b]/20 transition"
              />
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us about your travel plans..."
                className="w-full border border-neutral-200 rounded-lg px-4 py-3 outline-none focus:border-[#17a89b] focus:ring-2 focus:ring-[#17a89b]/20 transition resize-none"
              />
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-[#17a89b] hover:bg-[#128a7f] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                <Send className="w-4 h-4" /> Send Enquiry by Email
              </button>
              {sent && (
                <p className="text-center text-sm text-[#17a89b] font-medium">
                  Opening your email app&hellip; please press send to reach us.
                </p>
              )}
              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
                  "Hi Auracrest Haven, I'd like to enquire about a stay."
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> Enquire on WhatsApp
              </a>
              <div className="flex items-center gap-3 pt-1">
                <span className="h-px flex-1 bg-neutral-200" />
                <span className="text-xs text-neutral-400">or simply call us</span>
                <span className="h-px flex-1 bg-neutral-200" />
              </div>
              <a
                href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`}
                className="w-full flex items-center justify-center gap-2 border-2 border-[#17a89b] text-[#17a89b] hover:bg-[#e3faf7] font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" /> Call {CONTACT.phone}
              </a>
            </form>
          </div>
        </div>

        {/* Gallery */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
          {GALLERY.map((img, i) => (
            <img
              key={i}
              src={img}
              alt="gallery"
              className="w-full h-64 object-cover rounded-2xl shadow-sm hover:shadow-md transition-shadow"
            />
          ))}
        </div>

        {/* Google Map */}
        <div className="mt-12">
          <div className="rounded-2xl overflow-hidden shadow-sm border border-neutral-200">
            <iframe
              title="Auracrest Haven location map"
              src="https://www.google.com/maps?q=Auracrest%20Haven%2C%20Anachal%2C%20Sengulam%2C%20Munnar%2C%20Idukki%2C%20Kerala&z=13&output=embed"
              width="100%"
              height="420"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="text-center mt-4">
            <a
              href="https://www.google.com/maps/search/?api=1&query=Auracrest+Haven+Anachal+Sengulam+Munnar+Idukki+Kerala"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#17a89b] hover:text-[#128a7f] font-semibold text-sm"
            >
              <MapPin className="w-4 h-4" /> Open in Google Maps
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
